import React, { useEffect, useState } from "react";
import {
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  Printer,
  RefreshCw,
  ShoppingCart,
  TrendingUp,
  X,
} from "lucide-react";
import ReserveReportSummary from "./ReserveReportSummary";
import { buildReserveReportSummary } from "../shared/reserveReceipt";
import { buildXReportPrintSummaryRows } from "../shared/xReportPrintSummary";
import {
  countXReportArticles,
  sumXReportCategoryTotals,
} from "../shared/xReportCategoryTotals";

const formatMoney = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const XReportModal = ({ isOpen, onClose, currentUser, currentSession }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !currentSession?.id) return;
    const controller = new AbortController();
    void loadReportData(controller.signal);
    return () => controller.abort();
  }, [isOpen, currentSession]);

  const loadReportData = async (signal) => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(
        `/api/cash-sessions/${currentSession.id}/x-report`,
        signal ? { signal } : undefined,
      );
      if (!res.ok) {
        throw new Error("Échec du chargement");
      }
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Erreur chargement rapport X:", err);
      setError("Erreur lors du chargement du rapport X");
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptPrinterSettingsSafe = async () => {
    try {
      const res = await fetch(`/api/receipt-printer-settings`);
      if (!res.ok) return null;
      return (await res.json()) || null;
    } catch (_) {
      return null;
    }
  };

  const fetchRestaurantInfoSafe = async () => {
    try {
      const res = await fetch(`/api/restaurant-info`);
      if (!res.ok) return null;
      return (await res.json()) || null;
    } catch (_) {
      return null;
    }
  };

  const normalizeSpaces = (value) =>
    String(value || "").replace(/\u202F|\u00A0/g, " ").trim();

  const escapePrintHtml = (value) =>
    normalizeSpaces(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatPrintMoney = (value) => normalizeSpaces(formatMoney(value));

  const formatPrintDateTime = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return normalizeSpaces(String(value));
      return normalizeSpaces(d.toLocaleString("fr-FR"));
    } catch (_) {
      return "—";
    }
  };

  const formatPrintTime = (value) => {
    if (!value) return "Aucune vente";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return normalizeSpaces(String(value));
      return normalizeSpaces(d.toLocaleTimeString("fr-FR"));
    } catch (_) {
      return "Aucune vente";
    }
  };

  const buildUsbPrintCss = (paperWidthMm = 72) => {
    const raw = Number(paperWidthMm);
    const width = Number.isFinite(raw) && raw > 0 ? raw : 72;
    const clamped = Math.min(Math.max(width, 58), 80);
    return `
      @page { margin: 0; }
      html, body { margin: 0; padding: 0; }
      body { width: ${clamped}mm; max-width: ${clamped}mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    `;
  };

  const injectUsbPrintCss = (html, paperWidthMm = 72) => {
    const source = String(html || "");
    if (!source.trim()) return source;
    const styleTag = `<style>${buildUsbPrintCss(paperWidthMm)}</style>`;
    if (source.includes("</head>")) {
      return source.replace("</head>", `${styleTag}</head>`);
    }
    if (source.includes("<html")) {
      return source.replace("<html>", `<html><head>${styleTag}</head>`);
    }
    return `<html><head>${styleTag}</head><body>${source}</body></html>`;
  };

  const resolveUsbPrintOptions = (settings = {}) => {
    const configuredName = String(settings?.printer_name || "").trim();
    const sanitizedName =
      configuredName &&
      configuredName.toLowerCase() !== "ticket caisse"
        ? configuredName
        : undefined;
    const configuredWidth = Number(settings?.paper_width_mm);
    return {
      silent: true,
      paperWidthMm:
        Number.isFinite(configuredWidth) && configuredWidth > 0
          ? configuredWidth
          : 80,
      deviceName: sanitizedName,
    };
  };

  const printHtmlInHiddenIframe = (html, options = {}) =>
    new Promise((resolve, reject) => {
      try {
        const paperWidthMm = Number(options?.paperWidthMm) || 72;
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "820px";
        iframe.style.height = "1200px";
        iframe.style.border = "0";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
        iframe.setAttribute("aria-hidden", "true");

        const cleanup = () => {
          try {
            iframe.remove();
          } catch (_) {}
        };

        iframe.onload = () => {
          try {
            const w = iframe.contentWindow;
            if (!w) throw new Error("Fenêtre d'impression indisponible.");
            w.focus();
            w.print();
            window.setTimeout(() => {
              cleanup();
              resolve(true);
            }, 1200);
          } catch (err) {
            cleanup();
            reject(err);
          }
        };

        iframe.srcdoc = injectUsbPrintCss(html, paperWidthMm);
        document.body.appendChild(iframe);
      } catch (err) {
        reject(err);
      }
    });

  const printHtmlViaUsbPrinter = async (html, settings = {}) => {
    const options = resolveUsbPrintOptions(settings);
    if (window?.electronAPI?.printHtml) {
      await window.electronAPI.printHtml({ html, ...options });
      return;
    }
    await printHtmlInHiddenIframe(html, options);
  };

  const buildXReportPrintHtml = (data, restaurantInfo) => {
    const categories = Array.isArray(data?.categories) ? data.categories : [];
    const reserveSummary = buildReserveReportSummary(data?.reserves);
    const salesSummaryRows = buildXReportPrintSummaryRows({
      salesSummary: data?.sales_summary,
      reserves: data?.reserves,
      adjustments: data?.adjustments,
    });
    const payments = Array.isArray(data?.payment_methods)
      ? data.payment_methods
      : [];
    const xPrintModes = Array.isArray(data?.modes) ? data.modes : [];
    const cashMovements = Array.isArray(data?.cash_movements)
      ? data.cash_movements
      : [];

    const cashierLabel = `${data?.cashier?.first_name || ""} ${
      data?.cashier?.last_name || ""
    }`.trim();

    const grandTotalQty = countXReportArticles(categories);
    const grandTotalAmount = sumXReportCategoryTotals(categories);

    const restaurantName =
      restaurantInfo?.name && String(restaurantInfo.name).trim()
        ? String(restaurantInfo.name).trim()
        : "Restaurant";
    const restaurantAddress =
      restaurantInfo?.address && String(restaurantInfo.address).trim()
        ? String(restaurantInfo.address).trim()
        : "";
    const restaurantPhone =
      restaurantInfo?.phone && String(restaurantInfo.phone).trim()
        ? String(restaurantInfo.phone).trim()
        : "";

    const creditSaleFeatureEnabled = Boolean(
      data?.features?.credit_sale_enabled,
    );

    const row = (label, value, opts = {}) => {
      const valueClass = opts.valueClass || "";
      return `
        <div class="row">
          <span class="label">${label}</span>
          <span class="value ${valueClass}">${value}</span>
        </div>
      `;
    };

    const sectionTitle = (title) => `<div class="section-title">${title}</div>`;

    const productLabel = (product) => {
      if (!product) return "Produit";
      if (product.variation_name) {
        return `${product.name || "Produit"} · ${product.variation_name}`;
      }
      return product.name || "Produit";
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Rapport X</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              padding: 0;
              font-size: 11.5px;
              color: #000;
              background: #fff;
              font-weight: 600;
            }
            .ticket {
              width: 100%;
              max-width: none;
              margin: 0 auto;
              background: #fff;
              border: 0;
              border-radius: 0;
              padding: 8px 6px 10px;
            }
            .center { text-align: center; }
            .title { font-size: 15px; font-weight: 700; }
            .subtitle { font-size: 11.5px; margin-top: 2px; }
            .muted { color: #111; font-weight: 600; }
            .line { border-top: 1px solid #000; margin: 10px 0; }
            .section { margin: 10px 0; }
            .section-title {
              font-size: 10.5px;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              font-weight: 800;
              margin-bottom: 6px;
            }
            .row {
              display: grid;
              grid-template-columns: minmax(0, 1fr) auto;
              align-items: start;
              column-gap: 8px;
              margin: 2px 0;
            }
            .label {
              min-width: 0;
              overflow-wrap: anywhere;
              line-height: 1.25;
            }
            .value {
              text-align: right;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;
            }
            .strong { font-weight: 800; }
            .small { font-size: 11px; }
            .category {
              border: 1px dashed #000;
              border-radius: 10px;
              padding: 10px;
              margin: 8px 0;
            }
            .item {
              display: grid;
              grid-template-columns: minmax(0, 1fr) auto;
              align-items: start;
              column-gap: 8px;
              padding: 2px 0;
            }
            .item-name {
              min-width: 0;
              overflow-wrap: anywhere;
              line-height: 1.25;
            }
            .item-amt {
              text-align: right;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;
            }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="center">
              <div class="title">${normalizeSpaces(restaurantName)}</div>
              ${restaurantAddress ? `<div class="small">${normalizeSpaces(restaurantAddress)}</div>` : ""}
              ${restaurantPhone ? `<div class="small">Tel: ${normalizeSpaces(restaurantPhone)}</div>` : ""}
              <div class="line"></div>
              <div class="subtitle strong">RAPPORT X</div>
              ${
                currentSession?.id
                  ? `<div class="small">Session #${currentSession.id}</div>`
                  : ""
              }
              <div class="small muted">Impression: ${formatPrintDateTime(
                data?.report_date,
              )}</div>
            </div>

            <div class="line"></div>

            <div class="section">
              ${sectionTitle("Caissier & rapport")}
              ${row("Nom", normalizeSpaces(cashierLabel || "—"))}
              ${row("Identifiant", normalizeSpaces(data?.cashier?.username || "—"))}
            </div>

            <div class="section">
              ${sectionTitle("Temps & session")}
              ${row(
                "Session ouverte",
                data?.session?.start_time
                  ? formatPrintDateTime(data.session.start_time)
                  : "—",
              )}
              ${row("Première vente", formatPrintTime(data?.time_data?.first_sale))}
              ${row("Dernière vente", formatPrintTime(data?.time_data?.last_sale))}
            </div>

            <div class="line"></div>

            <div class="section">
              ${sectionTitle("Fonds")}
              ${row("Initial", `${formatPrintMoney(data?.cash_fund?.initial_amount)} F`)}
              ${row("Espèces en caisse", `${formatPrintMoney(data?.cash_fund?.current_amount)} F`, { valueClass: "strong" })}
              ${row("Solde théorique global", `${formatPrintMoney(data?.cash_fund?.total_collected ?? data?.cash_fund?.current_amount)} F`)}
            </div>

            <div class="section">
              ${sectionTitle("Ajustements (IN/OUT)")}
              ${row("Entrées", `+${formatPrintMoney(data?.adjustments?.cash_in)} F`)}
              ${
                creditSaleFeatureEnabled
                  ? row(
                      "Recouvrement",
                      `+${formatPrintMoney(data?.adjustments?.recovery_in || 0)} F`,
                    )
                  : ""
              }
              ${row("Sorties", `-${formatPrintMoney(data?.adjustments?.cash_out)} F`)}
              ${row(
                "Net",
                `${formatPrintMoney(
                  (data?.adjustments?.cash_in || 0) - (data?.adjustments?.cash_out || 0),
                )} F`,
              )}
            </div>

            ${
              cashMovements.length > 0
                ? `
                  <div class="section">
                    ${sectionTitle("Détail des mouvements")}
                    ${cashMovements
                      .map((movement) => {
                        const isDeposit = movement?.type === "deposit";
                        const typeLabel = isDeposit ? "Entrée" : "Sortie";
                        const sign = isDeposit ? "+" : "-";
                        return `
                          <div class="row">
                            <span class="label strong">${formatPrintTime(
                              movement?.created_at,
                            )} · ${typeLabel}</span>
                            <span class="value">${sign}${formatPrintMoney(
                              movement?.amount,
                            )} F</span>
                          </div>
                          <div class="small">Motif : ${escapePrintHtml(
                            movement?.reason || "Sans motif renseigné",
                          )}</div>
                          ${
                            movement?.cashier_name
                              ? `<div class="small muted">Par : ${escapePrintHtml(
                                  movement.cashier_name,
                                )}</div>`
                              : ""
                          }
                        `;
                      })
                      .join("")}
                  </div>
                `
                : ""
            }

            <div class="section">
              ${sectionTitle("Statistiques")}
              ${row("Tickets", formatPrintMoney(data?.statistics?.ticket_count || 0))}
              ${row("Articles", formatPrintMoney(data?.statistics?.items_sold || 0))}
              ${row(
                "Annulations (articles)",
                formatPrintMoney(data?.statistics?.cancelled_items || 0),
              )}
              ${row(
                "Panier moyen",
                `${formatPrintMoney(data?.statistics?.average_ticket || 0)} F`,
              )}
            </div>

            <div class="line"></div>

            <div class="section">
              ${sectionTitle("Moyens de paiement")}
              ${
                payments.length === 0
                  ? `<div class="small muted">Aucune transaction</div>`
                  : payments
                      .map(
                        (pm) => `
                          <div class="row">
                            <span class="label strong">${normalizeSpaces(
                              pm?.name || "Non défini",
                            )}</span>
                            <span class="value">${formatPrintMoney(
                              pm?.amount || 0,
                            )} F</span>
                          </div>
                          <div class="small muted">${Number(
                            pm?.percentage || 0,
                          ).toFixed(1)}%</div>
                        `,
                      )
                      .join("")
              }
            </div>

            <div class="line"></div>

            ${
              xPrintModes.length > 0
                ? `
                  <div class="section">
                    ${sectionTitle("Ventilation par service")}
                    ${xPrintModes
                      .map(
                        (m) => `
                          <div class="row">
                            <span class="label strong">${normalizeSpaces(m.mode)}</span>
                            <span class="value">${formatPrintMoney(m.net_sales)} F</span>
                          </div>
                          <div class="small muted">${m.count} commande${m.count > 1 ? "s" : ""}</div>
                        `,
                      )
                      .join("")}
                  </div>
                  <div class="line"></div>
                `
                : ""
            }

            <div class="section">
              ${sectionTitle("Détail par catégorie")}
              ${
                categories.length === 0
                  ? `<div class="small muted">Aucune vente enregistrée</div>`
                  : categories
                      .map((cat) => {
                        const products = Array.isArray(cat?.products)
                          ? cat.products
                          : [];
                        return `
                          <div class="category">
                            <div class="row">
                              <span class="label strong">${normalizeSpaces(
                                `${cat?.name || "Catégorie"} (${cat?.items_count || 0})`,
                              )}</span>
                              <span class="value strong">${formatPrintMoney(
                                cat?.total || 0,
                              )} F</span>
                            </div>
                            <div class="small muted">—</div>
                            ${products
                              .map(
                                (p) => `
                                  <div class="item">
                                    <div class="item-name">${normalizeSpaces(
                                      `${productLabel(p)} (×${p?.quantity || 0})`,
                                    )}</div>
                                    <div class="item-amt">${formatPrintMoney(
                                      p?.total || 0,
                                    )} F</div>
                                  </div>
                                `,
                              )
                              .join("")}
                          </div>
                        `;
                      })
                      .join("")
              }
              ${
                categories.length > 0
                  ? `
                    <div class="line"></div>
                    <div class="row">
                      <span class="label strong">Grand total (${formatPrintMoney(
                        grandTotalQty,
                      )} articles)</span>
                      <span class="value strong">${formatPrintMoney(
                        grandTotalAmount,
                      )} F</span>
                    </div>
                  `
                  : ""
              }
            </div>

            ${
              reserveSummary.issuedMinor || reserveSummary.cancelledIssueMinor
                ? `<div class="section">
                    <div class="line"></div>
                    ${sectionTitle("Consignations")}
                    ${reserveSummary.lines.map((line) => row(
                      `${formatPrintMoney(line.quantity)}x ${escapePrintHtml(line.label)}`,
                      `${formatPrintMoney(line.valueMinor / 100)} F`,
                    )).join("")}
                    ${row("Total", `${formatPrintMoney(reserveSummary.issuedMinor / 100)} F`, { valueClass: "strong" })}
                  </div>`
                : ""
            }
            ${
              reserveSummary.redeemedMinor || reserveSummary.restoredRedemptionMinor
                ? `<div class="section">
                    ${sectionTitle("Réserves utilisées")}
                    ${row("Total", `${formatPrintMoney(reserveSummary.redeemedMinor / 100)} F`, { valueClass: "strong" })}
                  </div>`
                : ""
            }

            <div class="section">
              <div class="line"></div>
              ${sectionTitle("Résumé ventes")}
              ${salesSummaryRows.map((entry) => row(
                escapePrintHtml(entry.label),
                `${entry.prefix}${formatPrintMoney(entry.amount)} F`,
                { valueClass: entry.strong ? "strong" : "" },
              )).join("")}
            </div>

            <div class="line"></div>
            <div class="center small">
              Merci pour votre visite !
              <br />
              Powered by MOO Sync - ${new Date().getFullYear()}
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const printReport = async () => {
    try {
      if (!reportData) return;
      setError("");

      const [settings, restaurantInfo] = await Promise.all([
        fetchReceiptPrinterSettingsSafe(),
        fetchRestaurantInfoSafe(),
      ]);

      if (settings?.connection_type === "ethernet") {
        const res = await fetch(`/api/receipt-printer/print-x-report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurant_name: restaurantInfo?.name || "",
            restaurant_address: restaurantInfo?.address || "",
            restaurant_phone: restaurantInfo?.phone || "",
            report: reportData,
          }),
        });
        if (!res.ok) {
          let message = "Erreur d'impression (Rapport X).";
          try {
            const data = await res.json();
            message = data?.error || message;
          } catch (_) {}
          throw new Error(message);
        }
        return;
      }

      const html = buildXReportPrintHtml(reportData, restaurantInfo);
      await printHtmlViaUsbPrinter(html, settings);
    } catch (err) {
      console.error("Erreur impression rapport X:", err);
      setError(err?.message || "Erreur lors de l'impression du rapport X");
    }
  };

  if (!isOpen) return null;

  const categories = reportData?.categories || [];
  const payments = reportData?.payment_methods || [];
  const modes = reportData?.modes || [];
  const firstSale = reportData?.time_data?.first_sale;
  const lastSale = reportData?.time_data?.last_sale;
  const creditSaleFeatureEnabled = Boolean(
    reportData?.features?.credit_sale_enabled,
  );

  const getProductLabel = (product) => {
    if (!product) return "Produit";
    if (product.variation_name) {
      return `${product.name || "Produit"} · ${product.variation_name}`;
    }
    return product.name || "Produit";
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm print:static print:bg-white">
      <div className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-[#0b1328] text-white shadow-2xl print:max-w-full print:rounded-none print:border-none">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-amber-500/15 via-indigo-500/15 to-slate-900 px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <FileText size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                Rapport X
              </p>
              <h2 className="text-xl font-semibold">
                État de caisse — {currentUser?.first_name}{" "}
                {currentUser?.last_name}
              </h2>
              {currentSession?.id && (
                <p className="text-xs text-white/60">
                  Session #{currentSession.id}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition print:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[78vh] overflow-y-auto px-6 py-5 print:max-h-none print:overflow-visible">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-amber-400" />
              <span className="ml-3 text-sm text-white/70">
                Chargement du rapport...
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          {!loading && reportData && (
            <div className="space-y-5">
              {/* Infos rapides */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200">
                    Caissier & rapport
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-white/80">
                    <div className="flex justify-between">
                      <span>Nom</span>
                      <span className="font-semibold">
                        {reportData.cashier?.first_name}{" "}
                        {reportData.cashier?.last_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Identifiant</span>
                      <span className="font-semibold">
                        {reportData.cashier?.username || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Impression</span>
                      <span className="font-semibold">
                        {new Date(
                          reportData.report_date,
                        ).toLocaleString("fr-FR")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200">
                    Temps & session
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-white/80">
                    <div className="flex justify-between">
                      <span>Session ouverte</span>
                      <span className="font-semibold">
                        {reportData.session?.start_time
                          ? new Date(
                              reportData.session.start_time,
                            ).toLocaleString("fr-FR")
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Première vente</span>
                      <span className="font-semibold">
                        {firstSale
                          ? new Date(firstSale).toLocaleTimeString("fr-FR")
                          : "Aucune vente"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Dernière vente</span>
                      <span className="font-semibold">
                        {lastSale
                          ? new Date(lastSale).toLocaleTimeString("fr-FR")
                          : "Aucune vente"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fonds & ajustements */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <DollarSign size={18} />
                      Fonds
                    </div>
                    <span className="text-xs text-emerald-200">
                      Initial / Actuel
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/70">Initial</span>
                      <span className="font-semibold">
                        {formatMoney(reportData.cash_fund?.initial_amount)} F
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Espèces en caisse</span>
                      <span className="font-semibold text-emerald-300">
                        {formatMoney(reportData.cash_fund?.current_amount)} F
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-1">
                      <span className="text-white/70">Solde théorique global</span>
                      <span className="font-semibold text-amber-200">
                        {formatMoney(reportData.cash_fund?.total_collected)} F
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-purple-500/10 to-purple-500/5 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Clock size={18} />
                      Ajustements
                    </div>
                    <span className="text-xs text-purple-200">IN / OUT</span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/70">Entrées</span>
                      <span className="font-semibold text-emerald-300">
                        +{formatMoney(reportData.adjustments?.cash_in)} F
                      </span>
                    </div>
                    {creditSaleFeatureEnabled && (
                      <div className="flex justify-between">
                        <span className="text-white/70">Recouvrement</span>
                        <span className="font-semibold text-emerald-300">
                          +{formatMoney(reportData.adjustments?.recovery_in || 0)} F
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/70">Sorties</span>
                      <span className="font-semibold text-red-300">
                        -{formatMoney(reportData.adjustments?.cash_out)} F
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2">
                      <span className="text-white/80">Net</span>
                      <span className="font-semibold text-purple-200">
                        {formatMoney(
                          (reportData.adjustments?.cash_in || 0) -
                            (reportData.adjustments?.cash_out || 0),
                        )}{" "}
                        F
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Statistiques & paiements */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <TrendingUp size={18} />
                    Statistiques
                  </div>
	                  <div className="mt-3 grid grid-cols-2 gap-3 text-center text-sm sm:grid-cols-4">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <p className="text-white/60 text-xs">Tickets</p>
                      <p className="text-lg font-semibold">
                        {reportData.statistics?.ticket_count || 0}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <p className="text-white/60 text-xs">Articles</p>
                      <p className="text-lg font-semibold">
                        {reportData.statistics?.items_sold || 0}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <p className="text-white/60 text-xs">Annulations</p>
                      <p className="text-lg font-semibold">
                        {reportData.statistics?.cancelled_items || 0}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <p className="text-white/60 text-xs">Panier moyen</p>
                      <p className="text-lg font-semibold">
                        {formatMoney(reportData.statistics?.average_ticket)} F
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CreditCard size={18} />
                    Moyens de paiement
                  </div>
                  <div className="mt-3 space-y-2 text-sm">
                    {payments.length === 0 && (
                      <p className="text-white/60">Aucune transaction</p>
                    )}
                    {payments.map((method) => (
                      <div
                        key={method.name}
                        className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-3 py-2"
                      >
                        <span className="font-semibold">
                          {method.name || "Non défini"}
                        </span>
                        <div className="text-right">
                          <p className="font-semibold">
                            {formatMoney(method.amount)} F
                          </p>
                          <p className="text-xs text-white/60">
                            {(method.percentage || 0).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ventilation par mode de service */}
              {modes.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <ShoppingCart size={18} />
                    Ventilation par service
                  </div>
                  <div className="mt-3 space-y-2 text-sm">
                    {modes.map((m) => (
                      <div
                        key={m.mode}
                        className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-3 py-2"
                      >
                        <span className="font-semibold">{m.mode}</span>
                        <div className="text-right">
                          <p className="font-semibold">
                            {formatMoney(m.net_sales)} F
                          </p>
                          <p className="text-xs text-white/60">
                            {m.count} commande{m.count > 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Détail catégories */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <ShoppingCart size={18} />
                  Détail par catégorie (variations incluses)
                </div>
                <div className="mt-3 space-y-3 text-sm">
                  {categories.length === 0 && (
                    <p className="text-white/60">Aucune vente enregistrée</p>
                  )}
                  {categories.map((category) => (
                    <div
                      key={category.name}
                      className="rounded-xl border border-white/10 bg-white/5 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">
                          {category.name} ({category.items_count || 0})
                        </span>
                        <span className="text-xs text-white/60">
                          Total: {formatMoney(category.total)} F
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {(category.products || []).map((product) => (
                          <div
                            key={`${product.name ?? ""}-${product.variation_name ?? ""}`}
                            className="flex justify-between rounded-lg px-2 py-1 text-white/80"
                          >
                            <span>
                              {getProductLabel(product)} (×
                              {product.quantity || 0})
                            </span>
                            <span className="font-semibold">
                              {formatMoney(product.total)} F
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {categories.length > 0 && (
                    <div className="flex items-center justify-between rounded-xl border border-white/20 bg-gradient-to-r from-amber-500/20 to-indigo-500/20 px-3 py-3 font-semibold">
                      <span>
                        Grand total (
                        {countXReportArticles(categories)}{" "}
                        articles)
                      </span>
                      <span>
                        {formatMoney(
                          sumXReportCategoryTotals(categories),
                        )}{" "}
                        F
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <ReserveReportSummary data={reportData.reserves} />
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-4">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <TrendingUp size={18} />
                  Résumé ventes
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-white/70">Brut</span><span className="font-semibold text-amber-200">{formatMoney(reportData.sales_summary?.gross_sales)} F</span></div>
                  <div className="flex justify-between"><span className="text-white/70">Remises</span><span className="font-semibold text-orange-300">-{formatMoney(reportData.sales_summary?.total_discounts)} F</span></div>
                  <div className="flex justify-between"><span className="text-white/70">À crédit</span><span className="font-semibold text-violet-300">-{formatMoney(reportData.sales_summary?.total_credit_sales || 0)} F</span></div>
                  <div className="flex justify-between"><span className="text-white/70">Réserves utilisées</span><span className="font-semibold text-emerald-300">-{formatMoney(Number(reportData.reserves?.redeemedMinor || 0) / 100)} F</span></div>
                  <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-white/90">Net</span><span className="font-semibold text-amber-300">{formatMoney(reportData.sales_summary?.net_sales)} F</span></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t border-white/10 bg-[#0b1328]/90 px-6 py-4 print:hidden">
          <div className="text-xs text-white/60">
            Généré le{" "}
            {reportData
              ? new Date(reportData.report_date).toLocaleString("fr-FR")
              : "-"}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void loadReportData()}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-white hover:border-white/20 hover:bg-white/10"
              title="Rafraîchir le rapport"
            >
              <RefreshCw size={16} />
              Rafraîchir
            </button>
            <button
              type="button"
              onClick={printReport}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:border-amber-400/60 hover:bg-white/15"
            >
              <Printer size={16} />
              Imprimer
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-white hover:border-white/20"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XReportModal;
